import React, { useEffect, useState } from 'react';
import { Button } from '@/components';
import { SideBarModal } from '@/components/Modal';
import { formatRupiah, formatThousands } from '@/utils';
import { DriverOutstanding } from '@/types/outstanding';

interface SettleOutstandingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (amount: number, notes: string) => void;
  isSubmitting?: boolean;
  outstanding: DriverOutstanding | null;
}

/**
 * A deliberate (partial) repayment — never applied automatically from a
 * rental top-up, so this is the only way an outstanding's balance moves
 * toward SETTLED short of a full write-off.
 */
const SettleOutstandingModal: React.FC<SettleOutstandingModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  outstanding,
}) => {
  const [amountDigits, setAmountDigits] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setAmountDigits('');
      setNotes('');
      setError('');
    }
  }, [isOpen]);

  const remaining = outstanding?.amountOutstanding ?? 0;

  const handleSubmit = () => {
    const amount = Number(amountDigits || '0');
    if (!amount || amount <= 0) {
      setError('Amount harus lebih dari 0');
      return;
    }
    if (amount > remaining) {
      setError(`Amount tidak boleh melebihi sisa ${formatRupiah(remaining)}`);
      return;
    }
    onSubmit(amount, notes.trim());
  };

  return (
    <SideBarModal
      isOpen={isOpen}
      onClose={onClose}
      position="center"
      width="md"
      maxHeight="auto"
      header={
        <div>
          <div className="text-base font-semibold text-gray-900">
            Settle Outstanding
          </div>
          <div className="text-xs text-gray-500">
            Catat pembayaran yang sudah diterima dari driver. Sisa saat ini:{' '}
            {formatRupiah(remaining)}.
          </div>
        </div>
      }
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button variant="white" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Memproses...' : 'Settle'}
          </Button>
        </div>
      }
      className="max-w-[520px]"
    >
      <div className="px-6 py-4 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Amount <span className="text-red-500">*</span>
          </label>
          <div
            className={`flex items-center border rounded-md px-3 focus-within:ring-2 focus-within:ring-primary ${
              error ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <span className="text-sm text-gray-500 mr-2">Rp</span>
            <input
              type="text"
              inputMode="numeric"
              value={formatThousands(amountDigits)}
              onChange={(e) => {
                setAmountDigits(e.target.value.replace(/\D/g, ''));
                if (error) setError('');
              }}
              placeholder="0"
              className="flex-1 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
            />
          </div>
          {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Contoh: cicilan pertama diterima tunai di hub"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>
    </SideBarModal>
  );
};

export default SettleOutstandingModal;
